import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ApiService, ChatService, LoggerService, QuestionMessage, SystemChatMessage, User } from 'angular-lib';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs/Subscription';
import { LoaderService } from '../../services/loader.service';
import { MenuService } from '../../services/menu.service';
import { ToastService } from '../../services/toast.service';
import { Observable } from 'rxjs/Observable';
import { DOCUMENT } from '@angular/common';

@Component({
    selector: 'ez-chat',
    template: require('./chat.component.html'),
    styles: [require('./chat.component.less')],
    encapsulation: ViewEncapsulation.None
})
export class ChatComponent implements OnInit, OnDestroy {
    public user: User;
    public me;
    public subs: Subscription[] = [];
    public loadMoreClicked: boolean = false;
    public chatInteractionList: any = [];
    public obsList: Observable<any>[] = [];
    public firstLoad: boolean = true;
    public message: string = '';

    @ViewChild('chatContainer') private chatContainer: ElementRef;

    constructor(
        private apiService: ApiService,
        private route: ActivatedRoute,
        private loader: LoaderService,
        private menuService: MenuService,
        private toastService: ToastService,
        private chatService: ChatService,
        @Inject(DOCUMENT) private document: any
    ) {
    }

    public ngOnInit() {
        this.subs.push(this.route.params.subscribe(params => {
            this.loader.show();
            let userObs = this.apiService.user(+params.id).share();
            this.chatService.setCurrentFollowedUser(+params.id);
            this.obsList.push(userObs);
            userObs.subscribe(user => {
                this.user = user;
            });

            let meObs = this.apiService.user().share();
            this.obsList.push(meObs);
            meObs.subscribe(user => {
                this.me = user;
                let messageObs = this.chatService.getFollowedUserOutputStream().share();
                this.obsList.push(messageObs.first());
                this.subs.push(messageObs.subscribe(
                    (lines) => {
                        this.chatInteractionList = lines;
                        this.checkQuestionAnswered();
                        if (this.loadMoreClicked) {
                            this.loadMoreClicked = false;
                            // if its not the first time (on load conversation)
                        } else if (!this.firstLoad) {
                            // scroll after the new message
                            this.scrollToEnd();
                        }
                    }
                ));

                this.subs.push(this.chatService.getErrorStream().subscribe((error: any) => {
                    if (typeof error.errorType !== 'undefined') {
                        let errorTranslationKey = '';
                        if (error.errorType === this.chatService.ERROR_MESSAGE) {
                            errorTranslationKey = 'chat.errors.message-emission';
                        } else if (error.errorType === this.chatService.ERROR_RESPONSE) {
                            errorTranslationKey = 'chat.errors.response-emission';
                        }
                        this.toastService.show(errorTranslationKey, ToastService.TYPE_ERROR);
                    }
                }));
                this.chatService.loadConversation();
            });

            Observable.forkJoin(this.obsList).subscribe(
                () => {
                    // first scroll to the end
                    this.scrollToEnd();
                    this.loader.hide();
                    // first conversation load over, so set the attribute to false
                    this.firstLoad = false;
                },
                () => {
                    this.loader.hide();
                }
            );
        }));
    }

    public ngOnDestroy() {
        this.subs.forEach(sub => sub.unsubscribe());
        this.chatService.unfollowUser();
        // mark chat unread bubble service as to be reloaded, if is already not
        if (!this.chatService.isUnreadBubbleMarkedForRefresh()) {
            this.chatService.markUnreadBubbleForRefresh();
        }
    }

    public loadMore() {
        if (this.loadMoreClicked === false) {
            this.loadMoreClicked = true;
            this.chatService.loadConversation();
        }
    }

    private scrollToEnd() {
        setTimeout(() => {
            window.scrollTo(0, this.chatContainer.nativeElement.scrollHeight);
        }, 100);
    }

    public onSubmit() {
        this.chatService.sendMessage(this.message);
        this.message = '';
        this.scrollToEnd();
        // so the form doesnt post
        return false;
    }

    public onKeyUp($e) {
        this.chatService.setTyping($e);
    }

    public onQuestionAnswered(data) {
        LoggerService.info('QUESTION ANSWERED : ');
        LoggerService.info(data);
        this.checkQuestionAnswered(data);
        this.menuService.setUser(this.user.id);
    }

    private checkQuestionAnswered(data?: {id: number, responseId: number}) {
        // if no data provided, check all groups
        if (typeof data === 'undefined') {
            // if this element is a question, check if it has been answered
            // slice is used to make a copy and reverse to reverse it (used for deletion, shift will reorganise the array)
            this.chatInteractionList.slice().reverse().forEach((interaction, index, array) => {
                // compute original array index
                let realIndex = array.length - 1 - index;
                if (interaction.type() === 'question' && interaction.userRequestResponseId > 0) {
                    // if this user request is a picture authorisation type and the user respond
                    // yes and isn't the last updated thing, delete it from the list
                    // (picture authorization will automatically show up as a system message)
                    if (
                        interaction.userRequestTypeId === 1
                        && interaction.userRequestResponseId === 1
                        && interaction !== this.chatService.getCurrentConversationLastUpdatedMessage()
                    ) {
                        // so delete this line
                        this.chatInteractionList.splice(realIndex, 1);
                    } else {
                        // transform it to a system message
                        this.chatInteractionList[realIndex] = this.createSystemMessageFromQuestion(interaction);
                    }
                // check only those who have not been checked yet
                } else if (interaction.type() === 'system' && typeof interaction.content === 'undefined') {
                    if (interaction.element === SystemChatMessage.TYPE_PICTURE_AUTHORIZATION) {
                        interaction.content = 'private-auth.has-accept.' + interaction.direction;
                        interaction.setIcon('check');
                        interaction.setIconSize(SystemChatMessage.ICON_SIZE_SMALL);
                        interaction.setIconColor(SystemChatMessage.ICON_COLOR_GREEN);
                        interaction.addPseudoBeforeContent(true);
                    } else if (interaction.element === SystemChatMessage.TYPE_START_DISCUSSION) {
                        interaction.content = 'chat.send-first-message';
                        interaction.setIcon('flash');
                    }
                }
            });
        // if data provided, check only one
        } else {
            this.chatInteractionList.forEach((interaction, index) => {
                if (
                    interaction.type() === 'question'
                    && interaction.id === data.id
                ) {
                    interaction.userRequestResponseId = data.responseId;
                    this.chatInteractionList[index] = this.createSystemMessageFromQuestion(interaction);
                }
            });
        }
    }

    private createSystemMessageFromQuestion(question: QuestionMessage): SystemChatMessage {
        let systemMessage = new SystemChatMessage({
            // invert discussion for creating the feedback
            direction: question.direction === 'from' ? 'to' : 'from',
            createdAt: question.createdAt
        });
        switch (question.userRequestResponseId) {
            case 1:
                // adding {{pseudo}} to display pseudo
                systemMessage.content = 'private-auth.has-accept.' + question.direction;
                systemMessage.setIcon('check');
                systemMessage.addPseudoBeforeContent(true);
                systemMessage.setIconSize(SystemChatMessage.ICON_SIZE_SMALL);
                systemMessage.setIconColor(SystemChatMessage.ICON_COLOR_GREEN);
                break;
            case 2:
                systemMessage.content = 'private-auth.has-refused.' + question.direction;
                systemMessage.setIcon('close');
                systemMessage.addPseudoBeforeContent(true);
                systemMessage.setIconSize(SystemChatMessage.ICON_SIZE_SMALL);
                systemMessage.setIconColor(SystemChatMessage.ICON_COLOR_RED);
                break;
        }
        return systemMessage;
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/@angularclass/hmr-loader!./src/app/components/chat/chat.component.ts